import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Plus, DoorOpen, Users, MoreVertical, Pencil, Trash2, Calendar,
  Timer, Save, Repeat, PlayCircle, GraduationCap, ChevronRight, Clock
} from 'lucide-react';
import CreateClassroomDialog from '@/components/classroom/CreateClassroomDialog';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString([], { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : null;
const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString([], { day:'2-digit', month:'short' }) : '—';
const fmtHm  = (mins) => {
  const h = Math.floor(mins/60), m = Math.round(mins%60);
  return `${h}h ${m}m`;
};
const toLocalIso = (iso) => iso ? new Date(iso).toISOString().slice(0,10) : '';
const startOfMonthIso = () => {
  const d = new Date(); d.setDate(1); d.setHours(0,0,0,0);
  return d.toISOString().slice(0,10);
};
const todayIso = () => new Date().toISOString().slice(0,10);

function StatCard({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold text-center">{label}</div>
      <div className="mt-1 text-2xl font-display font-extrabold text-center">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-500 text-center">{sub}</div>}
    </div>
  );
}

function RoomRow({ r, isStudent, onDelete, onEdit, onTemplate, onOpen, onJoinLive }) {
  const startedAt = r.session_started_at || null;
  const endedAt = r.session_ended_at || (!r.active ? r.updated_at : null);
  const durationMin = (() => {
    if (startedAt && endedAt) {
      const d = (new Date(endedAt) - new Date(startedAt)) / 60000;
      return Math.max(0, Math.round(d));
    }
    return r.duration_minutes || 0;
  })();
  const isLive = r.active && r.is_live;

  return (
    <div
      data-testid={`classroom-row-${r.id}`}
      className={`rounded-xl border p-3 flex items-start gap-3 transition-all hover:shadow-sm ${
        isLive ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
      }`}
    >
      <div className="w-12 shrink-0 text-center rounded-lg bg-white border border-slate-200 py-1.5">
        <div className="text-base font-display font-extrabold leading-none">
          {r.scheduled_at ? new Date(r.scheduled_at).getDate().toString().padStart(2,'0') : '—'}
        </div>
        <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">
          {r.scheduled_at ? new Date(r.scheduled_at).toLocaleString([], { month:'short' }) : ''}
        </div>
        <div className="text-[9px] text-slate-400">
          {r.scheduled_at ? new Date(r.scheduled_at).getFullYear() : ''}
        </div>
        <div className="text-[9px] font-mono text-slate-500 mt-0.5">
          {r.scheduled_at ? new Date(r.scheduled_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : ''}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold text-base truncate">{r.name}</div>
          {isLive && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> En vivo
            </span>
          )}
          {r.recurrence === 'weekly' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
              <Repeat className="w-3 h-3"/> Semanal
            </span>
          )}
        </div>
        <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span>Por <b>{r.teacher_name || '—'}</b>{r.school_name ? ` (${r.school_name})` : ' (chess4kings)'}</span>
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5"/>
            Alumnos: {(r.participants || []).filter(p => p.role === 'student').length}
          </span>
        </div>
        <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-blue-500"/> Programado: {fmtDate(r.scheduled_at) || '—'}</span>
          {startedAt && <span className="inline-flex items-center gap-1"><PlayCircle className="w-3.5 h-3.5 text-emerald-600"/> Inició: {new Date(startedAt).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</span>}
          {endedAt && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-500"/> Fin: {new Date(endedAt).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</span>}
          <span className="inline-flex items-center gap-1"><Timer className="w-3.5 h-3.5 text-violet-500"/> Duración: {durationMin >= 120 ? '2+ horas' : `${durationMin} min`}</span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Button
            data-testid={`classroom-open-${r.id}`}
            size="sm"
            onClick={() => onOpen(r)}
            className={`rounded-full ${isLive ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isLive ? 'Entrar en vivo' : 'Ver detalles'} <ChevronRight className="w-3.5 h-3.5 ml-1"/>
          </Button>
          <span className="text-xs font-mono text-slate-400 tracking-widest">CÓDIGO · {r.code}</span>
        </div>
      </div>

      {!isStudent && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={`classroom-menu-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-slate-400 hover:text-rose-600">
              <Trash2 className="w-4 h-4"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid={`classroom-rename-${r.id}`} onClick={()=>onEdit(r)}>
              <Pencil className="w-4 h-4 mr-2"/> Editar
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`classroom-tpl-${r.id}`} onClick={()=>onTemplate(r)}>
              <Save className="w-4 h-4 mr-2"/> Guardar como plantilla
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`classroom-delete-${r.id}`} onClick={()=>onDelete(r)} className="text-rose-600">
              <Trash2 className="w-4 h-4 mr-2"/> Borrar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export default function ClassroomsList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Dialogs
  const [openCreate, setOpenCreate] = useState(false);
  const [openJoin, setOpenJoin] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openSaveTpl, setOpenSaveTpl] = useState(false);
  const [tplForm, setTplForm] = useState({ name:'', description:'', duration_minutes:60 });
  const [tplSource, setTplSource] = useState(null);
  const [form, setForm] = useState({ name:'', scheduled_at:'', duration_minutes:60, group_id:'', template_id:'', recurrence:false });
  const [editing, setEditing] = useState(null);
  const [code, setCode] = useState('');

  // Filters
  const [scope, setScope] = useState('self');    // self | group | all
  const [from, setFrom] = useState(startOfMonthIso());
  const [to, setTo] = useState(todayIso());

  const isStudent = user?.role === 'student';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'school_admin';

  const load = useCallback(() => {
    api.get('/classrooms').then(r => setRooms(r.data));
  }, []);
  useEffect(() => {
    load();
    if (!isStudent) {
      api.get('/groups').then(r => setGroups(r.data)).catch(()=>{});
      api.get('/classroom-templates').then(r => setTemplates(r.data)).catch(()=>{});
    }
  }, [isStudent, load]);

  // Apply filters
  const filtered = useMemo(() => {
    const start = from ? new Date(from + 'T00:00:00').getTime() : 0;
    const end = to ? new Date(to + 'T23:59:59').getTime() : Number.MAX_SAFE_INTEGER;
    return rooms.filter(r => {
      const ref = new Date(r.scheduled_at || r.created_at).getTime();
      if (ref < start || ref > end) return false;
      if (isAdmin && scope !== 'all') {
        if (scope === 'self' && r.teacher_user_id !== user.id) return false;
        if (scope === 'group' && r.teacher_user_id === user.id) return false;
      }
      return true;
    });
  }, [rooms, from, to, scope, isAdmin, user?.id]);

  // Sections
  const now = Date.now();
  // E2 · Vista del alumno: separar en curso vs terminadas usando ended_at real.
  const studentInProgress = rooms.filter(r => r.active === true && !r.ended_at);
  const studentEnded = rooms.filter(r => r.active === false || !!r.ended_at);
  const scheduled = filtered.filter(r => r.active && !r.is_live && r.scheduled_at && new Date(r.scheduled_at).getTime() > now);
  const inProgress = filtered.filter(r => r.active && r.is_live);
  const openWaiting = filtered.filter(r => r.active && !r.is_live && (!r.scheduled_at || new Date(r.scheduled_at).getTime() <= now));
  const completed = filtered.filter(r => !r.active);

  // Stats
  // Fase B-5G: clasificación Grupo vs Individual — soporta group_id y group_ids[]
  const isGroupClass = (r) =>
    !!r?.group_id ||
    (Array.isArray(r?.group_ids) && r.group_ids.length > 0);

  const totalStudents = new Set(
    filtered.flatMap(r => (r.participants || []).filter(p => p.role === 'student').map(p => p.user_id))
  ).size;
  const totalDuration = filtered.reduce((acc, r) => acc + (Number(r.duration_minutes) || 0), 0);
  const individualCount = filtered.filter(r => !isGroupClass(r)).length;
  const groupCount = filtered.filter(r => isGroupClass(r)).length;
  const individualDur = filtered.filter(r => !isGroupClass(r)).reduce((a,r)=>a+(r.duration_minutes||0),0);
  const groupDur = filtered.filter(r => isGroupClass(r)).reduce((a,r)=>a+(r.duration_minutes||0),0);

  const create = async () => {
    try {
      const { data } = await api.post('/classrooms', {
        name: form.name || 'Nueva clase',
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        duration_minutes: Number(form.duration_minutes) || 60,
        group_id: form.group_id || null,
        template_id: form.template_id || null,
        recurrence: form.recurrence ? 'weekly' : null,
      });
      const invited = (data.participants || []).length - 1;
      toast.success(`"${data.name}" creada · código ${data.code}${invited > 0 ? ` · ${invited} alumnos invitados` : ''}`);
      setOpenCreate(false);
      setForm({ name:'', scheduled_at:'', duration_minutes:60, group_id:'', template_id:'', recurrence:false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveTemplate = async () => {
    try {
      await api.post('/classroom-templates', {
        name: tplForm.name, description: tplForm.description,
        duration_minutes: Number(tplForm.duration_minutes) || 60,
        fen: tplSource?.fen || null,
      });
      toast.success('Plantilla guardada');
      setOpenSaveTpl(false); setTplForm({ name:'', description:'', duration_minutes:60 }); setTplSource(null);
      api.get('/classroom-templates').then(r => setTemplates(r.data));
    } catch { toast.error('Error'); }
  };

  const openTplFromRoom = (r) => {
    setTplSource(r);
    setTplForm({ name: r.name + ' (plantilla)', description:'', duration_minutes: r.duration_minutes || 60 });
    setOpenSaveTpl(true);
  };
  const join = async () => {
    try {
      const { data } = await api.post('/classrooms/join', { code: code.toUpperCase().trim() });
      toast.success('Te uniste a la clase');
      setOpenJoin(false); setCode('');
      nav(`/classroom/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Código no válido'); }
  };
  const startEdit = (r) => {
    const localDt = r.scheduled_at ? new Date(r.scheduled_at).toISOString().slice(0,16) : '';
    setEditing(r);
    setForm({ name: r.name, scheduled_at: localDt, duration_minutes: r.duration_minutes || 60 });
    setOpenEdit(true);
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/classrooms/${editing.id}`, {
        name: form.name,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        duration_minutes: Number(form.duration_minutes) || 60,
      });
      toast.success('Clase actualizada');
      setOpenEdit(false); setEditing(null); load();
    } catch { toast.error('Error al actualizar'); }
  };
  const removeRoom = async (r) => {
    if (!window.confirm(`¿Borrar el salón "${r.name}"? No se puede deshacer.`)) return;
    try {
      await api.delete(`/classrooms/${r.id}`);
      toast.success('Salón borrado'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openRoom = (r) => nav(`/classroom/${r.id}`);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Salones"
        subtitle={isStudent ? 'Únete con el código de tu profesor' : 'Crea, gestiona y revisa tus clases en vivo'}
        testId="classrooms-header"
        right={
          <div className="flex items-center gap-2">
            {isStudent ? (
              <Dialog open={openJoin} onOpenChange={setOpenJoin}>
                <DialogTrigger asChild>
                  <Button data-testid="join-classroom-btn" className="rounded-full bg-blue-600 hover:bg-blue-700">
                    <DoorOpen className="w-4 h-4 mr-1"/> Unirme por código
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Entrar a un salón</DialogTitle></DialogHeader>
                  <Input data-testid="classroom-code" placeholder="Código de 6 caracteres" className="uppercase font-mono text-lg tracking-widest"
                    value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())} maxLength={6}/>
                  <DialogFooter><Button data-testid="classroom-join-btn" onClick={join} disabled={code.length < 4}>Unirme</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            ) : (
              <Button data-testid="create-classroom-btn" onClick={()=>setOpenCreate(true)}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-1"/> Crear clase
              </Button>
            )}
          </div>
        }
      />

      <CreateClassroomDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={()=>load()}
      />

      {/* Filters */}
      {!isStudent && (
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {isAdmin && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-40 rounded-lg" data-testid="scope-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="self">Mías</SelectItem>
                <SelectItem value="group">De otros</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} className="w-40" data-testid="date-from"/>
            <span className="text-slate-400">→</span>
            <Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} className="w-40" data-testid="date-to"/>
          </div>
        </div>
      )}

      {/* Stats */}
      {!isStudent && (
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <StatCard
            testId="stat-total-classes"
            label="Total Classes"
            value={filtered.length}
            sub={`Individual: ${individualCount} | Grupo: ${groupCount}`}
          />
          <StatCard
            testId="stat-total-duration"
            label="Total Duration"
            value={fmtHm(totalDuration)}
            sub={`Individual: ${fmtHm(individualDur)} | Grupo: ${fmtHm(groupDur)}`}
          />
          <StatCard
            testId="stat-total-students"
            label="Total Active Students"
            value={totalStudents}
            sub={totalStudents > 0 ? 'Alumnos únicos participantes' : '—'}
          />
        </div>
      )}

      {/* Sections */}
      {isStudent ? (
        rooms.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
            No estás en ningún salón. Pídele el código a tu profesor.
          </div>
        ) : (
          <div className="space-y-8">
            <Section title="Clases en curso" count={studentInProgress.length} color="text-emerald-700" testId="student-section-active">
              {studentInProgress.length === 0 ? (
                <div className="text-sm text-slate-400" data-testid="student-active-empty">No tienes clases en curso.</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {studentInProgress.map(r => <StudentCard key={r.id} r={r} />)}
                </div>
              )}
            </Section>
            <Section title="Clases terminadas" count={studentEnded.length} color="text-slate-700" testId="student-section-ended">
              {studentEnded.length === 0 ? (
                <div className="text-sm text-slate-400" data-testid="student-ended-empty">Aún no tienes clases terminadas.</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {studentEnded.map(r => <StudentCard key={r.id} r={r} ended />)}
                </div>
              )}
            </Section>
          </div>
        )
      ) : (
        <div className="space-y-8">
          {inProgress.length > 0 && (
            <Section title="En vivo" count={inProgress.length} color="text-emerald-700" testId="section-live">
              {inProgress.map(r => <RoomRow key={r.id} r={r} isStudent={isStudent}
                onDelete={removeRoom} onEdit={startEdit} onTemplate={openTplFromRoom} onOpen={openRoom}/>)}
            </Section>
          )}
          {scheduled.length > 0 && (
            <Section title="Programadas" count={scheduled.length} color="text-blue-700" testId="section-scheduled">
              {scheduled.map(r => <RoomRow key={r.id} r={r} isStudent={isStudent}
                onDelete={removeRoom} onEdit={startEdit} onTemplate={openTplFromRoom} onOpen={openRoom}/>)}
            </Section>
          )}
          {openWaiting.length > 0 && (
            <Section title="Abiertas" count={openWaiting.length} color="text-amber-700" testId="section-open">
              {openWaiting.map(r => <RoomRow key={r.id} r={r} isStudent={isStudent}
                onDelete={removeRoom} onEdit={startEdit} onTemplate={openTplFromRoom} onOpen={openRoom}/>)}
            </Section>
          )}
          {completed.length > 0 && (
            <Section title="Completadas" count={completed.length} color="text-slate-700" testId="section-completed">
              {completed.map(r => <RoomRow key={r.id} r={r} isStudent={isStudent}
                onDelete={removeRoom} onEdit={startEdit} onTemplate={openTplFromRoom} onOpen={openRoom}/>)}
            </Section>
          )}
          {filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
              <GraduationCap className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
              Sin clases en el rango seleccionado. Cambia las fechas o crea una nueva.
            </div>
          )}
        </div>
      )}

      {/* Save template dialog */}
      <Dialog open={openSaveTpl} onOpenChange={setOpenSaveTpl}>
        <DialogContent>
          <DialogHeader><DialogTitle>Guardar plantilla</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label>
              <Input data-testid="tpl-name" value={tplForm.name} onChange={(e)=>setTplForm(f=>({...f,name:e.target.value}))}/></div>
            <div><Label>Descripción</Label>
              <Input data-testid="tpl-desc" value={tplForm.description} onChange={(e)=>setTplForm(f=>({...f,description:e.target.value}))}/></div>
            <div><Label>Duración (min)</Label>
              <Input type="number" min={5} step={5} value={tplForm.duration_minutes} onChange={(e)=>setTplForm(f=>({...f,duration_minutes:e.target.value}))}/></div>
            {tplSource?.fen && <div className="text-xs text-slate-500 font-mono truncate">FEN: {tplSource.fen}</div>}
          </div>
          <DialogFooter><Button data-testid="tpl-save-btn" onClick={saveTemplate} disabled={!tplForm.name}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar salón</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label>
              <Input data-testid="classroom-edit-name" value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha y hora</Label>
                <Input data-testid="classroom-edit-date" type="datetime-local" value={form.scheduled_at} onChange={(e)=>setForm(f=>({...f,scheduled_at:e.target.value}))}/></div>
              <div><Label>Duración (min)</Label>
                <Input data-testid="classroom-edit-duration" type="number" min={5} step={5} value={form.duration_minutes} onChange={(e)=>setForm(f=>({...f,duration_minutes:e.target.value}))}/></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="classroom-edit-save" onClick={saveEdit}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, count, color, children, testId }) {
  return (
    <section data-testid={testId}>
      <h2 className={`font-display font-extrabold text-lg mb-3 ${color}`}>
        {title} <span className="text-slate-400 font-mono text-sm">({count})</span>
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function StudentCard({ r, ended }) {
  return (
    <div data-testid={`classroom-${r.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-lg transition-all">
      <Link to={`/classroom/${r.id}`}>
        <div className="flex items-center gap-2">
          <div className="font-display font-bold text-base truncate">{r.name}</div>
          {ended && (
            <span data-testid={`student-ended-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-slate-200 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest shrink-0">
              Terminada
            </span>
          )}
        </div>
        <div className="font-mono text-xs text-slate-500 tracking-widest">CÓDIGO · {r.code}</div>
        <div className="mt-2 text-xs text-slate-600">{fmtDate(r.scheduled_at) || 'Sin fecha'} · {r.duration_minutes} min</div>
      </Link>
    </div>
  );
}
